/**
 * AEM's App Integration Framework already renders `<app-root>` with its config attributes
 * (`data-base`, `customconfiguration`, `contextconfiguration`, …) baked in — there is no separate
 * wrapper element to query, and no `<app-root>` to create; Angular's `bootstrapApplication` finds
 * this exact element on its own once the main bundle runs. The AEM dialog only lets authors point
 * at a single non-module script URL though, not a `<script type="module">` tag, so this classic
 * (non-module) script is the only thing AEM embeds directly. It fetches `entrypoints.json`
 * (produced by `scripts/create-entrypoints.js`) and injects the real module bundles at runtime via
 * the DOM API, where `type="module"` works normally.
 */
import { injectLink, injectScript, injectStyle } from "./launcher-helper/load-file";
import { startLoadingScreen, stopLoadingScreen } from "./launcher-helper/loading-screen";
import { APP_READY_EVENT } from "./app-ready-event";

/**
 * If the app never reports ready — a failed bootstrap, a dead API — the placeholder must not sit
 * there for good. Generous on purpose: the measured first paint on stage is ~2 s, and cutting a
 * slow but working load short is worse than a few extra seconds of spinner.
 */
const READY_TIMEOUT_MS = 15000;

interface Entrypoints {
    main?: string;
    styles?: string;
    /** Chunks main.js imports statically. Nothing lazy — see create-entrypoints.js. */
    preload?: string[];
}

/**
 * Runs `reveal` once, either when the app reports ready or when the timeout runs out.
 *
 * The two `requestAnimationFrame`s matter: the event fires while Angular is still inside the change
 * detection run that flipped `loaded`, so the app's own markup has not been laid out yet. Swapping
 * in that moment would show an empty host for a frame.
 */
const whenAppReady = (reveal: () => void): void => {
    let done = false;

    const runOnce = (): void => {
        if (done) {
            return;
        }
        done = true;

        requestAnimationFrame(() => requestAnimationFrame(reveal));
    };

    document.addEventListener(APP_READY_EVENT, runOnce, { once: true });
    window.setTimeout(runOnce, READY_TIMEOUT_MS);
};

async function run(): Promise<void> {
    const element = document.querySelector<HTMLElement>("app-root");

    if (!element) {
        console.warn("[nutz-launcher]", "no <app-root> found");
        return;
    }

    element.style.display = "none";
    startLoadingScreen(element);

    whenAppReady(() => {
        // Order is deliberate: make the host visible first, drop the placeholder second, both in
        // one synchronous block. One layout pass, one paint, no intermediate state to shift in.
        element.style.display = "";
        stopLoadingScreen();
        console.log("[nutz-launcher]", "finished");
    });

    const urlBase = element.getAttribute("data-base") ?? "";

    // No cache buster any more. nginx serves this file `no-cache`, so a revalidation is a 304
    // instead of a full body, and `?v=${Date.now()}` only guaranteed a full download on the
    // critical path — measured at 128 ms before the first bundle could even start.
    const resp = await fetch(`${urlBase}spa/entrypoints.json`);
    const manifest: Entrypoints = await resp.json();

    // Stylesheet first: it does not block the module graph and the app needs it to paint.
    if (manifest.styles) {
        injectStyle(urlBase + "spa/" + manifest.styles);
    }

    // Without these the browser only discovers the shared chunks after parsing main.js, which
    // measured as two extra waterfall waves at 581 ms and 631 ms.
    for (const chunk of manifest.preload ?? []) {
        injectLink({ rel: "modulepreload", href: urlBase + "spa/" + chunk });
    }

    if (manifest.main) {
        injectScript(urlBase + "spa/" + manifest.main);
    }
}

void run().catch((err: unknown) => {
    console.error("[nutz-launcher]", err);
});
